import React, { useState, useEffect } from 'react';
import type { Role } from '../../api/roles';

interface EditRoleModalProps {
  show: boolean;
  role: Role | null;
  onHide: () => void;
  onSubmit: (roleId: string, displayName: string) => Promise<void>;
}

const EditRoleModal: React.FC<EditRoleModalProps> = ({ show, role, onHide, onSubmit }) => {
  const [displayName, setDisplayName] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (role) {
      setDisplayName(role.displayName);
      setError(null);
    }
  }, [role, show]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!role || !displayName.trim() || displayName.trim() === role.displayName) return;
    setIsLoading(true);
    setError(null);
    try {
      await onSubmit(role.id, displayName.trim());
      onHide();
    } catch (err) {
      console.error(err);
      setError(err instanceof Error ? err.message : 'Failed to update role');
    } finally {
      setIsLoading(false);
    }
  };

  if (!show) return null;

  return (
    <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content">
          <div className="modal-header bg-primary text-white">
            <h5 className="modal-title">
              <i className="bi bi-pencil-square me-2" />
              Edit Role
            </h5>
            <button type="button" className="btn-close btn-close-white" onClick={onHide} disabled={isLoading} />
          </div>
          <form onSubmit={handleSubmit}>
            <div className="modal-body">
              {error && (
                <div className="alert alert-danger mb-3" role="alert">
                  <i className="bi bi-exclamation-triangle me-2" />
                  {error}
                </div>
              )}
              {role && (
                <>
                  <div className="mb-3">
                    <label className="form-label">System Name</label>
                    <input
                      type="text"
                      className="form-control bg-light"
                      value={role.name}
                      readOnly
                      disabled
                    />
                  </div>
                  <div className="mb-3">
                    <label htmlFor="editRoleDisplayName" className="form-label">Display Name</label>
                    <input
                      type="text"
                      className="form-control"
                      id="editRoleDisplayName"
                      value={displayName}
                      onChange={(e) => setDisplayName(e.target.value)}
                      placeholder="e.g. Committee Member"
                      required
                    />
                  </div>
                </>
              )}
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={onHide} disabled={isLoading}>
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={isLoading || !role || !displayName.trim() || displayName.trim() === role.displayName}
              >
                {isLoading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" />
                    Saving...
                  </>
                ) : (
                  <>
                    <i className="bi bi-check2 me-2" />
                    Save
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default EditRoleModal;
